import Component from '@glimmer/component'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { isHTMLSafe } from '@ember/template'
import { getCellValue, type CustomCellSignature } from 'teacher-ui-client/components/stable-table/basic-table/cell'

function arrayIncludes(searchArray: unknown[], element: unknown): boolean {
  return searchArray.includes(element)
}

function arrayMatches(firstArray: unknown[], secondArray: unknown[]): boolean {
  const set1 = new Set(firstArray)
  const set2 = new Set(secondArray)

  if (set1.size !== set2.size) return false

  const mergedSet = new Set([...set1, ...set2])

  return mergedSet.size === set1.size && mergedSet.size === set2.size
}

/**
 * Use this component in your BasicTable column definitions, and provide it the following config;
 *
 * config.selectedIds       - an array of the currently selected id's
 * config.allSelectableIds  - an array of all possible selected id's for the current scope. This is used so the header
 *                            checkbox knows when all rows are selected.
 * config.onSelectClick     - an action to call when checkbox is clicked. It will be given the new array of selected ids.
 *
 * This component will use the (optional) cell value as either;
 * a) the label (for a header cell) or
 * b) the aria-label (for a body cell)
 *
 * And make sure your rows have an 'id' field, of unique ids.
 */
export default class SelectableCell extends Component<CustomCellSignature> {
  get config() {
    return this.args.tableMeta.config
  }

  get row() {
    return this.args.tableMeta.rowObject
  }

  get value() {
    const value = getCellValue(this.args.tableMeta)
    return typeof value === 'string' || isHTMLSafe(value) ? value : ''
  }

  get selectedIds() {
    return this.config?.selectedIds ?? []
  }

  get allSelectableIds() {
    return this.config?.allSelectableIds ?? []
  }

  onSelectAll = (isAllSelected: boolean) => {
    this.config?.onSelectClick?.(isAllSelected ? [] : this.allSelectableIds)
  }

  onSelect = (id: unknown) => {
    if (typeof id === 'string' || typeof id === 'number') {
      const isSelected = this.selectedIds.includes(id)
      let newSelectedIds
      if (isSelected) {
        // deselect
        newSelectedIds = this.selectedIds.filter((selectedId) => selectedId !== id)
      } else {
        // select
        newSelectedIds = [...this.selectedIds, id]
      }

      this.config?.onSelectClick?.(newSelectedIds)
    }
  }

  <template>
    {{#if @isHeader}}
      {{#let (arrayMatches this.selectedIds this.allSelectableIds) as |isAllSelected|}}
        <label data-test-cell-selectable class="m-0 inline-block cursor-pointer text-center" ...attributes>
          {{this.value}}
          <input
            data-test-select-all-checkbox
            type="checkbox"
            class="cursor-pointer"
            checked={{isAllSelected}}
            {{on "click" (fn this.onSelectAll isAllSelected)}}
          />
        </label>
      {{/let}}
    {{else}}
      <label
        data-test-cell-selectable
        class="m-0 inline-block cursor-pointer text-center"
        aria-label={{this.value}}
        ...attributes
      >
        <input
          data-test-select-checkbox={{this.row.id}}
          type="checkbox"
          class="cursor-pointer"
          checked={{arrayIncludes this.selectedIds this.row.id}}
          {{on "click" (fn this.onSelect this.row.id)}}
        />
      </label>
    {{/if}}
  </template>
}
